import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import callAPI from '../../../common/call-api';
import ThemedInput from '../../../common/components/ThemedInput';
import { Button } from '../../../common/components';
import ErrorList from '../../../common/components/ErrorList';
import {
  TitleBar,
  DocTitle,
  ServiceDialog,
} from '../../../common/components/Layout/StandardPageTemplate/Context';
import withQuery from '../../../common/components/withQuery';
import CurrentUserActions from '../../../redux-modules/current-user';
import LoggedInRedirect from './LoggedInRedirect';
import Template from '../components/Template';
import CodeInput from '../components/CodeInput';
import styles from './Login.css';

const CODE_EXPIRY_SECONDS = 15 * 60;

const formatTime = totalSeconds => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

class Login extends React.Component {
  static propTypes = {
    location: PropTypes.object,
  };

  constructor(props) {
    super(props);
    const message = (props.location.query || {}).message;
    this.state = {
      isLoading: false,
      loginSuccess: false,
      individualKey: null,
      serverError: false,
      errors: {
        form: message ? [message] : [],
      },
      mode: 'password',
      email: (props.location.query || {}).email || '',
      code: '',
      codeExpirySeconds: CODE_EXPIRY_SECONDS,
    };
  }

  componentWillUnmount() {
    this.clearCodeTimer();
  }

  clearCodeTimer = () => {
    if (this.codeTimerId) {
      clearInterval(this.codeTimerId);
      this.codeTimerId = null;
    }
  };

  startCodeTimer = () => {
    this.clearCodeTimer();
    this.setState({ codeExpirySeconds: CODE_EXPIRY_SECONDS });
    this.codeTimerId = setInterval(() => {
      this.setState(prevState => ({
        codeExpirySeconds: Math.max(0, prevState.codeExpirySeconds - 1),
      }));
    }, 1000);
  };

  handleLoginFailure = (message, updateField) => {
    //clear password
    updateField({ fieldName: 'password', value: '' });

    return this.setState(prevState => ({
      ...prevState,
      isLoading: false,
      errors: {
        ...prevState.errors,
        form: [message],
      },
    }));
  };

  handleLogin = ({ form, updateField }) => {
      //Remove Any Previously Cached Session Storage
  sessionStorage.clear();
    // clear any existing errors
    this.setState(prevState => ({ ...prevState, errors: { form: [] } }));

    this.setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));

    const { username, password } = form;
    // callApi to get authentication and handle response
    callAPI('/api/cuss/credentials', {
      method: 'POST',
      body: JSON.stringify({
        Username: username,
        Password: password,
      }),
    }).then(
      ({ resp, json }) => {
        if (resp.ok) {
          // save relevant customer data to local storage
          this.props.actions.user.login({
            id: json.key,
            token: resp.headers.get('authorizationtoken'),
            type: json.personaType,
          });
          //ga('set', 'userId', json.key);

          // update state with success
          return this.setState(prevState => ({
            ...prevState,
            isLoading: false,
            loginSuccess: true,
            individualKey: json.key,
          }));
        }
        return this.handleLoginFailure(json.message, updateField);
      },
      ({ json, resp }) => {
        if (resp.status === 500) {
          return this.setState({ serverError: true });
        }
        return this.handleLoginFailure(json.message, updateField);
      }
    );
  };

  handleEmailChange = value => {
    this.setState({ email: value });
    return value;
  };

  handleRequestCode = evt => {
    if (evt && evt.preventDefault) {
      evt.preventDefault();
    }

    if (!this.state.email.trim().length) {
      return this.setState(prevState => ({
        ...prevState,
        errors: { form: ['Please enter your email address'] },
      }));
    }

    this.setState(prevState => ({
      ...prevState,
      isLoading: true,
      errors: { form: [] },
    }));

    return callAPI(
      `/api/cuss/credentials/logincode?email=${encodeURIComponent(this.state.email)}`,
      { method: 'POST' }
    ).then(
      ({ resp, json }) => {
        if (resp.status === 204) {
          this.startCodeTimer();
          return this.setState(prevState => ({
            ...prevState,
            isLoading: false,
            mode: 'code',
            code: '',
            errors: { form: [] },
          }));
        }
        const message =
          (json && json.message) || 'Something went wrong. Please try again.';
        return this.setState(prevState => ({
          ...prevState,
          isLoading: false,
          errors: { form: [message] },
        }));
      },
      ({ resp, json }) => {
        if (resp.status === 500) {
          return this.setState({ serverError: true, isLoading: false });
        }
        const message =
          (json && json.message) || 'Something went wrong. Please try again.';
        return this.setState(prevState => ({
          ...prevState,
          isLoading: false,
          errors: { form: [message] },
        }));
      }
    );
  };

  handleCodeChange = code => {
    this.setState({ code });
  };

  handleCodeSubmit = evt => {
    evt.preventDefault();

    this.setState(prevState => ({
      ...prevState,
      isLoading: true,
      errors: { form: [] },
    }));

    callAPI('/api/cuss/credentials/logincode/validate', {
      method: 'POST',
      body: JSON.stringify({
        Email: this.state.email,
        AccessCode: this.state.code,
      }),
    }).then(
      ({ resp, json }) => {
        if (resp.ok) {
          this.props.actions.user.login({
            id: json.key,
            token: resp.headers.get('authorizationtoken'),
            type: json.personaType,
          });

          return this.setState(prevState => ({
            ...prevState,
            isLoading: false,
            loginSuccess: true,
            individualKey: json.key,
          }));
        }

        return this.setState(prevState => ({
          ...prevState,
          isLoading: false,
          code: '',
          errors: { form: ['Invalid Code. Please reenter'] },
        }));
      },
      ({ resp }) => {
        if (resp.status === 500) {
          return this.setState({ serverError: true, isLoading: false });
        }
        return this.setState(prevState => ({
          ...prevState,
          isLoading: false,
          code: '',
          errors: { form: ['Invalid Code. Please reenter'] },
        }));
      }
    );
  };

  render() {
    // if login was successful, go the appropriate location
    if (this.state.loginSuccess) {
      return (
        <LoggedInRedirect
          location={this.props.location}
          id={this.state.individualKey}
        />
      );
    }
    else if(this.props.location.query && this.props.location.query.returnUrl)
    {
      var dest = this.props.location.query.returnUrl.split('/');
      var data = [
        "advocacy",
        "business-resources",
        "news",
        "research-center",
        "legal-center",
        "member-vantage",
        "new-membership-benefits"
      ];
      if(dest.length > 1 && data.includes(dest[dest.length - 2])){
        return (
          <LoggedInRedirect
            location={this.props.location}
          />
        );
      }
    }

    const { location: { query } } = this.props;

    if (this.state.mode === 'code') {
      return (
        <div>
          <DocTitle title="Login | Engage | NFIB" />
          {this.state.serverError ? <ServiceDialog /> : null}
          <TitleBar title="" />
          <Template isLoading={this.state.isLoading} title="Login">
            <ErrorList errors={this.state.errors.form} />
            <p className={styles.emailDisplay}>{this.state.email}</p>
            <form onSubmit={this.handleCodeSubmit}>
              <CodeInput value={this.state.code} onChange={this.handleCodeChange} />
              <p className={styles.enterCodeLabel}>Enter Code</p>
              <p className={styles.codeExpiry}>
                Code expires in {formatTime(this.state.codeExpirySeconds)}
              </p>
              <div className={styles.buttonContainer}>
                <Button type="submit" label="Submit" />
              </div>
            </form>
            <p className={styles.fixedMessage}>
              If you did not receive a code in your email, click Resend Code
              or contact NFIB Member Care at (800) 634-2669.
            </p>
            <div className={styles.linkContainer}>
              {this.state.isLoading ? (
                <span className={styles.resendDisabled}>Resend Code</span>
              ) : (
                <a
                  href="#"
                  onClick={this.handleRequestCode}
                  className={styles.forgotLink}
                >
                  Resend Code
                </a>
              )}
            </div>
          </Template>
        </div>
      );
    }

    const loginFields = [
      {
        Component: ThemedInput,
        name: 'username',
        type: 'email',
        label: 'Email Address',
        required: true,
        onChange: this.handleEmailChange,
      },
      {
        Component: ThemedInput,
        name: 'password',
        type: 'password',
        required: true,
      },
      () => (
        <div className={styles.linkContainer} key="forgot-link">
          <Link to="/auth/forgot" className={styles.forgotLink}>
            Forgot Password
          </Link>
        </div>
      ),
      () => (
        <div className={styles.codeLinkContainer} key="code-link">
          <a
            href="#"
            onClick={this.handleRequestCode}
            className={styles.forgotLink}
          >
            Log-in With Code
          </a>
        </div>
      ),
    ];
    return (
      <div>
        <DocTitle title="Login | Engage | NFIB" />
        {this.state.serverError ? <ServiceDialog /> : null}
        <TitleBar title="" />
        <Template
          formState={{
            username: this.state.email || query.email || '',
            password: '',
          }}
          formSubmit={this.handleLogin}
          formInputs={loginFields}
          formErrors={this.state.errors.form}
          isLoading={this.state.isLoading}
          title="Login"
        />
      </div>
    );
  }
}

export default withQuery(
  connect(null, dispatch => ({
    actions: { user: bindActionCreators(CurrentUserActions, dispatch) },
  }))(Login)
);



// WEBPACK FOOTER //
// ./src/App/auth/Views/Login.js